/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {MouseEvent} from 'react';
import {useDispatch} from 'react-redux';
import {makeStyles} from '@material-ui/core/styles';
import isNil from 'lodash.isnil';
import useEnvQueryParam from '@ion/applications/src/hooks/useEnvQueryParam';
import {AppLink} from '@ion/applications/src/link/AppLink';
import {translate} from '@ion/core/src/locale/locale';
import {isDeviceAlert} from '@ion/core/src/services/AlertUtils';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {getQueryString} from '@ion/core/src/utils/ObjectUtil';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import {dlsColor} from '@ion/dls/src/theme/theme';
import useThemeContext from '@ion/dls/src/theme/useThemeContext';
import {Notification} from '@ion/dls/src/types/extensions';
import {DLSTheme} from '@ion/dls/src/types/types';
import {setNotificationsPanelStatus} from '../../store/actions/notifications';

export interface ViewEventCtaProps {
  notification?: Notification;
}

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    viewEventCta : (props: {isCurrentTheme: boolean}) => ({
      marginLeft : props.isCurrentTheme ? '0' : '25px',
      padding    : props.isCurrentTheme ? '0px 16px' : '4px 4px',
      fontSize   : props.isCurrentTheme ? '13px' : '14px',
      '& a'      : {
        color          : dlsColor(props.isCurrentTheme ? 'darkestBlue' : 'aaBlue'),
        textDecoration : 'none',
        '&:hover'      : {
          textDecoration : props.isCurrentTheme ? 'none' : 'underline'
        },
        '&:active, &:focus' : {
          color               : dlsColor(props.isCurrentTheme ? 'skyBlue' : 'grey1'),
          textDecoration      : 'underline',
          textDecorationColor : dlsColor(props.isCurrentTheme ? 'skyBlue' : 'grey1')
        }
      },
      ...(props.isCurrentTheme && {
        fontFamily    : 'Inter',
        fontWeight    : 600,
        lineHeight    : '14px',
        letterSpacing : '0.5px',
        textTransform : 'capitalize !important'
      })
    })
  };
});

const excludedAlerts = {
  'iOn Maintenance Alert'            : true,
  'iOn Maintenance Due Alert'        : true,
  'iOn Associated Tag Missing Alert' : true,
  'iOn Missing Tag Alert'            : true,
  'iOn Tag Found Alert'              : true,
  'iOn Associated Tag Found Alert'   : true
};

/**
 * "View Event" deep link CTA component suitable for passing to the DLS
 * NotificationCard component via its viewEventCta prop. This component
 * CTA exists separately from the NotificationCard component because the
 * NotificationCard component is in the DLS package, and this component
 * incorporates the AppLink component which is in the applications package.
 * Since the applications package depends on the DLS package, if this
 * component were part of the NotificationCard in the DLS, it would create
 * a circular dependency.
 *
 * ```typescript
 *
 *  import ViewEventCta from './ViewEventCta';
 *
 *  <NotificationCard
 *    notification={notification}
 *    viewEventCta={<ViewEventCta notification={notification} />}
 *    ... />
 *
 * ```
 *
 */
export const ViewEventCta = ({
  notification
}: ViewEventCtaProps) => {
  const {
    isCurrent : isCurrentTheme
  } = useThemeContext();

  const dispatch             = useDispatch();
  const classes              = useStyles({isCurrentTheme});
  const env                  = useEnvQueryParam();
  const isDeviceNotification = isDeviceAlert(notification);

  const {
    assetId,
    createdOn,
    device,
    avlMessageUuid,
    alertTypeName
  } = notification;

  const isExcludedAlert = excludedAlerts[alertTypeName];

  const isValid = (!isNil(assetId) || isDeviceNotification) && !isNil(createdOn) && !isNil(avlMessageUuid);

  if (isValid && !isExcludedAlert) {
    const page       = isDeviceNotification ? 'reports' : 'tracking2';
    const reportName = isDeviceNotification ? 'shipmentHistory' : 'assetHistory';
    const idKey      = isDeviceNotification ? 'deviceId' : 'assetId';
    const id         = isDeviceNotification ? entityId(device?.href) : assetId;
    const href       = `/${page}?${getQueryString({
      ...env,
      report  : reportName,
      eventId : avlMessageUuid,
      date    : createdOn,
      [idKey] : id
    })}`;

    const onClick = function(e: MouseEvent) {
      e.stopPropagation();
      if (isCurrentTheme) {
        dispatch(setNotificationsPanelStatus(false));
      }
    };

    return (
      <TertiaryButton className={classes.viewEventCta} onClick={onClick}>
        <AppLink href={href}>
          {translate('notifications:panel.viewEvent')}
        </AppLink>
      </TertiaryButton>
    );
  }
  return null;
};

export default ViewEventCta;
